// formationsMeta.json and searchIndex.json are specific to Comparateur
// For Social, it will use socialMeta.json and socialSearchIndex.json
// import formationsMeta from "./formationsMeta.json" // Keep commented or specific to a context if this file is general util
// import searchIndex from "./searchIndex.json"    // Keep commented

// Debug flag - set to true to enable detailed console logs
const DEBUG = false;

/**
 * Search entities (formations, establishments, etc.) based on a query string
 * @param {string} query - The search query
 * @param {Object} indexFile - The search index mapping words to entity IDs (e.g., searchIndex.json)
 * @param {Object} metaFile - The metadata for all entities (e.g., formationsMeta.json)
 * @param {Array<string>} entityTypes - Array of entity types to include (e.g., ['formation', 'establishment'])
 * @param {number} maxResults - Maximum number of results to return
 * @returns {Array} - Sorted array of matching entities with relevance scores
 */
export const searchEntities = (query, indexFile, metaFile, entityTypes = ['formation'], maxResults = 10) => {
  if (!query || !indexFile || !metaFile || !entityTypes || entityTypes.length === 0) {
    if (DEBUG) console.log("[searchEntities] Invalid input", { query, indexFile: !!indexFile, metaFile: !!metaFile, entityTypes });
    return [];
  }
  
  const normalizedQuery = normalizeText(query);
  if (!normalizedQuery) {
    if (DEBUG) console.log("[searchEntities] Empty normalized query");
    return [];
  }
  
  const queryWords = normalizedQuery.split(/\s+/).filter(word => word.length > 0);
  if (queryWords.length === 0) {
    if (DEBUG) console.log("[searchEntities] No query words");
    return [];
  }
  
  const candidateScores = new Map();
  
  // 1. Exact phrase match for multi-word queries
  if (queryWords.length > 1 && indexFile[normalizedQuery]) {
    for (const id of indexFile[normalizedQuery]) {
      const current = candidateScores.get(id) || new Set();
      queryWords.forEach(qw => current.add(qw));
      candidateScores.set(id, current);
    }
  }

  // 2. Term in indexFile contains the whole query (useful for partial phrase starts)
  if (queryWords.length > 1) {
      for (const term in indexFile) {
      if (term.includes(normalizedQuery)) {
            for (const id of indexFile[term]) {
                const current = candidateScores.get(id) || new Set();
                queryWords.forEach(qw => current.add(qw));
                candidateScores.set(id, current);
        }
      }
    }
  }
  
  // 3. Individual word matches
  for (const word of queryWords) {
    if (indexFile[word]) {
      for (const id of indexFile[word]) {
         const current = candidateScores.get(id) || new Set();
         current.add(word);
         candidateScores.set(id, current);
      }
    }
  }
  
  // 4. Partial matching (if not enough candidates or to boost relevant partials)
  if (candidateScores.size < maxResults * 3) { // Increased threshold a bit
    const processedPartialTerms = new Set(); // Track terms already used for partial matching to avoid over-processing

    for (const word of queryWords) {
      if (word.length < 3) continue; // Only do partial for words of reasonable length
      
      let partialMatchCountForWord = 0;
      const maxPartialMatchesPerQueryWord = 15; // Limit per query word

      for (const termInIndex in indexFile) {
        if (termInIndex.length < 3 || termInIndex === word) continue;

        if (termInIndex.includes(word) || word.includes(termInIndex)) { // Broader partial match
          if (processedPartialTerms.has(termInIndex)) continue;
          processedPartialTerms.add(termInIndex);

          for (const id of indexFile[termInIndex]) {
            const current = candidateScores.get(id) || new Set();
            current.add(word); // Credit the original query word that led to this partial match
            candidateScores.set(id, current);
          }
          partialMatchCountForWord++;
          if (partialMatchCountForWord >= maxPartialMatchesPerQueryWord) break;
        }
      }
    }
  }
  
  if (candidateScores.size === 0) {
    if (DEBUG) console.log("[searchEntities] No candidates found after matching phases.");
    return [];
  }
  if (DEBUG) console.log(`[searchEntities DEBUG] Found ${candidateScores.size} candidate items initially.`);
  
  const textCache = new Map();
  const results = [];
  
  for (const [itemId, matchedQueryWordsSet] of candidateScores.entries()) {
    const itemData = metaFile[itemId];

    if (itemData && entityTypes.includes(itemData.type)) {
      const entity = { id: itemId, ...itemData };
      const matchDetails = DEBUG ? [] : null;
      
      const primaryDisplayName = entity.displayName || ''; 
      const secondaryName = entity.type === 'formation' ? (entity.nomEtablissement || '') : '';
      const commune = entity.commune || '';
      
      const getCachedNormalizedText = (text) => {
        if (!text) return '';
        if (!textCache.has(text)) textCache.set(text, normalizeText(text));
        return textCache.get(text);
      };
      
      const normPrimary = getCachedNormalizedText(primaryDisplayName);
      const normSecondary = getCachedNormalizedText(secondaryName);
      const normCommune = getCachedNormalizedText(commune);
      
      const matchedWordsCount = matchedQueryWordsSet.size;
      let relevanceScore = matchedWordsCount * 1000;
      if (DEBUG) matchDetails.push(`Base from ${matchedWordsCount} index words: ${relevanceScore}`);

      // Scoring bonuses
      if (normPrimary.includes(normalizedQuery)) { relevanceScore += 800; if (DEBUG) matchDetails.push(`FullQ in Primary: +800`); }
      else { queryWords.forEach(w => { if (normPrimary.includes(w)) { relevanceScore += 150; if (DEBUG) matchDetails.push(`Word "${w}" in Primary: +150`); }}); }

      if (normSecondary) {
        if (normSecondary.includes(normalizedQuery)) { relevanceScore += 500; if (DEBUG) matchDetails.push(`FullQ in Secondary: +500`); }
        else { queryWords.forEach(w => { if (normSecondary.includes(w)) { relevanceScore += 75; if (DEBUG) matchDetails.push(`Word "${w}" in Secondary: +75`); }}); }
      }
      if (normCommune) {
        if (normCommune.includes(normalizedQuery)) { relevanceScore += 300; if (DEBUG) matchDetails.push(`FullQ in Commune: +300`); }
        else { queryWords.forEach(w => { if (normCommune.includes(w)) { relevanceScore += 50; if (DEBUG) matchDetails.push(`Word "${w}" in Commune: +50`); }}); }
      }
      
      if (queryWords.length > 1) {
          for (let i = 0; i < queryWords.length - 1; i++) {
          const pair = `${queryWords[i]} ${queryWords[i+1]}`;
          if (normPrimary.includes(pair) || (normSecondary && normSecondary.includes(pair)) || (normCommune && normCommune.includes(pair))) {
            relevanceScore += 250; if (DEBUG) matchDetails.push(`Pair "${pair}": +250`);
          }
        }
      }
      
      // Bonus if more query words were matched in the item's own indexed terms
      // This is implicitly handled by matchedWordsCount, but we can add a small kicker
      if (matchedWordsCount > queryWords.length / 2) {
          relevanceScore += matchedWordsCount * 10; // Small bonus based on how many unique query words hit
          if (DEBUG) matchDetails.push (`Bonus for ${matchedWordsCount} matched query words: +${matchedWordsCount * 10}`);
      }
      
      if (DEBUG) {
        matchDetails.push(`FINAL SCORE: ${relevanceScore}`);
        console.log(`[searchEntities SCORING] Item: ${entity.displayName} (Type: ${entity.type}), Score: ${relevanceScore}, Details:`, matchDetails);
      }
      
      results.push({ ...entity, relevanceScore, matchDetails: DEBUG ? matchDetails : undefined });
    } else {
      if (DEBUG && itemData) console.log(`[searchEntities FILTER] Skipping ${itemData.displayName} (Type: ${itemData.type}) - not in ${entityTypes.join()}`);
      else if (DEBUG && !itemData) console.log(`[searchEntities FILTER] Skipping ${itemId} - not in metaFile.`);
    }
  }
  
  results.sort((a, b) => b.relevanceScore - a.relevanceScore);
  if (DEBUG) console.log(`[searchEntities FINAL RESULTS] Top ${maxResults}:`, results.slice(0, maxResults).map(r => ({n:r.displayName, s:r.relevanceScore, t:r.type, id:r.id })));
  return results.slice(0, maxResults);
};

// Old searchFormations is now replaced by searchEntities.
// Components should be updated to use searchEntities.

export const debounce = (func, delay = 200) => {
  let timeoutId;
  return (...args) => {
    clearTimeout(timeoutId);
    return new Promise((resolve) => {
      timeoutId = setTimeout(() => { resolve(func(...args)); }, delay);
    });
  };
};

// Updated debouncedSearchEntities
export const debouncedSearchEntities = debounce(searchEntities, 200);

// Simple cache for search results
const searchResultsCache = new Map();
const MAX_CACHE_SIZE = 50; // Example cache size

// Updated cachedSearchEntities
export const cachedSearchEntities = (query, indexFile, metaFile, entityTypes = ['formation'], maxResults = 10) => {
  const cacheKey = `${normalizeText(query)}|${entityTypes.join(',')}|${maxResults}`;
  if (searchResultsCache.has(cacheKey)) {
    if (DEBUG) console.log(`[cachedSearchEntities] Cache HIT for: ${cacheKey}`);
    return searchResultsCache.get(cacheKey);
  }
  if (DEBUG) console.log(`[cachedSearchEntities] Cache MISS for: ${cacheKey}`);
  const results = searchEntities(query, indexFile, metaFile, entityTypes, maxResults);
  searchResultsCache.set(cacheKey, results);
  if (searchResultsCache.size > MAX_CACHE_SIZE) {
    const oldestKey = searchResultsCache.keys().next().value;
    searchResultsCache.delete(oldestKey);
  }
  return results;
};

// Recommended function to use for searching, combines caching and debouncing
export const optimizedSearch = debounce(cachedSearchEntities, 250); // Slightly longer debounce for optimized version

export const normalizeText = (text) => {
  if (!text) return "";
  return text
    .toString() // Ensure it's a string
    .toLowerCase()
    .normalize("NFD") // Decompose accented characters
    .replace(/[\u0300-\u036f]/g, "") // Remove diacritical marks (accents)
    .replace(/[\'",\(\)\[\]\.\/_-]+/g, ' ') // Replace separators with space
    .replace(/\s+/g, ' ') // Replace multiple spaces with a single space
    .trim();
};

// This function might be specific to how Comparateur loads its data
// Social component loads its JSON files directly via import.
export const loadSearchData = async () => {
  // This function is likely not needed if JSON is imported directly
  // For dynamic loading, it could be:
  // const formationsMeta = await import('./formationsMeta.json');
  // const searchIndex = await import('./searchIndex.json');
  // return { formationsMeta: formationsMeta.default, searchIndex: searchIndex.default };
  console.warn("loadSearchData in search-utils.js is likely deprecated for Social component use. Ensure data is loaded correctly.");
  return { searchIndex: {}, metaFile: {} }; 
};

// This function is specific to the old structure of formationsMeta.json from Comparateur
// It might not be directly applicable to the new socialMeta.json structure without adaptation.
export const metaToFormation = (meta) => {
  // This function is highly specific to the expected structure of a 'formation' item
  // and how it might have been used. Review if it's still needed.
  if (!meta || meta.type !== 'formation') return null; 
  return {
    id: meta.id,
    nomFormation: meta.nomFormation || meta.displayName,
    nomEtablissement: meta.nomEtablissement,
    commune: meta.commune,
    // ... other fields that were expected by components using this mapping
  };
};